import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, KeyRound, Inbox, Users,
  LogOut, Plus, Pencil, Trash2, UploadCloud, X, GripVertical, Search,
} from "lucide-react";
import { adminApi, publicApi } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMENITY_KEYS = [
  "exclusive", "parking", "pool", "elevator", "sea_view", "beach_access",
  "garden", "terrace", "ac", "heating", "security", "gym", "spa", "sauna",
  "wine_cellar", "home_cinema", "smart_home", "solar_panels", "staff_quarters",
];

const emptyProp = {
  title: { es: "", en: "", de: "", ru: "" },
  description: { es: "", en: "", de: "", ru: "" },
  type: "villa",
  location: "",
  country: "Spain",
  reference: "",
  price: 0,
  bedrooms: 0,
  bathrooms: 0,
  area_m2: 0,
  plot_m2: 0,
  year_built: null,
  images: [],
  videos: [],
  features: [],
  off_market: false,
  featured: false,
  is_investment: false,
  investor_closed: false,
  is_rental: false,
  rent_price: 0,
  rental_period: "week",
  expected_yield: "",
  ticket_size: "",
  horizon: "",
  status: "available",
  seo_title: { es: "", en: "", de: "", ru: "" },
  seo_description: { es: "", en: "", de: "", ru: "" },
  seo_keywords: "",
  image_alts: [],
  ...Object.fromEntries(AMENITY_KEYS.map((k) => [k, false])),
};

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "properties", label: "Properties", icon: Building2 },
  { key: "rentals", label: "Rentals", icon: KeyRound },
  { key: "inquiries", label: "Inquiries", icon: Inbox },
  { key: "clients", label: "Clients", icon: Users },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [props, setProps] = useState([]);
  const [inqs, setInqs] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("jk_admin_user") || "null"); } catch { return null; }
  })();

  useEffect(() => {
    if (!localStorage.getItem("jk_admin_jwt")) {
      navigate("/admin");
      return;
    }
    refresh();
  }, [navigate]);

  const refresh = useCallback(async () => {
    try {
      const [p, i, c] = await Promise.all([
        publicApi.listProperties({ limit: 500 }),
        adminApi.listInquiries(),
        axios.get(`${API}/admin/clients`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("jk_admin_jwt")}` },
        }).then((r) => r.data),
      ]);
      setProps(p);
      setInqs(i);
      setClients(c);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("jk_admin_jwt");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    }
  }, [navigate]);

  function logout() {
    localStorage.removeItem("jk_admin_jwt");
    localStorage.removeItem("jk_admin_user");
    localStorage.removeItem("jk_admin_token");
    navigate("/admin");
  }

  async function saveProperty() {
    try {
      const payload = { ...editing };
      ["price", "bedrooms", "bathrooms", "area_m2", "plot_m2", "rent_price"].forEach((k) => {
        payload[k] = Number(payload[k]) || 0;
      });
      payload.year_built = payload.year_built ? Number(payload.year_built) : null;
      if (typeof payload.features === "string") {
        payload.features = payload.features.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      if (!payload.reference?.trim()) {
        toast.error("Reference is required");
        return;
      }
      if (payload.id) {
        await adminApi.updateProperty(payload.id, payload);
      } else {
        delete payload.id;
        delete payload.created_at;
        await adminApi.createProperty(payload);
      }
      toast.success("Saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }
  async function removeProperty(id) {
    if (!window.confirm("Delete this property?")) return;
    await adminApi.deleteProperty(id);
    toast.success("Deleted");
    refresh();
  }

  const rentalList = props.filter((p) => p.is_rental);
  const salesList = props.filter((p) => !p.is_rental);
  const filteredList = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (p) =>
        (p.reference || "").toLowerCase().includes(q) ||
        (p.location || "").toLowerCase().includes(q) ||
        (p.title?.en || "").toLowerCase().includes(q) ||
        (p.title?.ru || "").toLowerCase().includes(q)
    );
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] text-slate-900 flex" data-testid="admin-cms">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0f172a] text-slate-100 flex-shrink-0 flex flex-col">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="font-serif text-2xl leading-none">JK Invest</div>
          <div className="text-[10px] uppercase tracking-widest2 text-slate-400 mt-1">CMS Panel</div>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = tab === n.key;
            return (
              <button
                key={n.key}
                onClick={() => { setTab(n.key); setEditing(null); }}
                data-testid={`admin-tab-${n.key}`}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  active ? "bg-slate-800 text-white border-l-2 border-amber-400" : "text-slate-300 hover:bg-slate-800/50"
                }`}
              >
                <Icon size={16} strokeWidth={1.8} />
                {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="px-2 py-3 text-xs text-slate-400 truncate">
            {user?.email || "Admin"}
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-2 py-2 text-xs uppercase tracking-widest2 text-slate-300 hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold capitalize">{tab}</h1>
            <div className="text-xs text-slate-500 mt-0.5">
              {tab === "properties" && `${salesList.length} properties for sale`}
              {tab === "rentals" && `${rentalList.length} rentals`}
              {tab === "inquiries" && `${inqs.length} inquiries`}
              {tab === "clients" && `${clients.length} registered clients`}
              {tab === "dashboard" && "Overview"}
            </div>
          </div>
          {(tab === "properties" || tab === "rentals") && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 border border-slate-200 rounded px-3 py-1.5 bg-white">
                <Search size={14} className="text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  data-testid="admin-search"
                  className="outline-none text-sm w-52"
                />
              </div>
              <button
                onClick={() => setEditing({ ...emptyProp, is_rental: tab === "rentals" })}
                data-testid="admin-new-property"
                className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs uppercase tracking-widest2 px-4 py-2 rounded hover:bg-slate-700"
              >
                <Plus size={14} /> New
              </button>
            </div>
          )}
        </header>

        <div className="p-8">
          {tab === "dashboard" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard label="Properties for sale" value={salesList.length} />
              <StatCard label="Rentals" value={rentalList.length} />
              <StatCard label="Inquiries" value={inqs.length} />
              <StatCard label="Registered clients" value={clients.length} />
              <StatCard label="Off-market" value={props.filter((p) => p.off_market).length} />
              <StatCard label="Investments" value={props.filter((p) => p.is_investment).length} />
            </div>
          )}

          {(tab === "properties" || tab === "rentals") && (
            <PropertyTable
              rows={filteredList(tab === "rentals" ? rentalList : salesList)}
              onEdit={(p) => setEditing({ ...emptyProp, ...p, title: { ...emptyProp.title, ...(p.title || {}) }, description: { ...emptyProp.description, ...(p.description || {}) } })}
              onDelete={removeProperty}
            />
          )}

          {tab === "inquiries" && <InquiryTable rows={inqs} onDelete={async (id) => { if (!window.confirm("Delete?")) return; await adminApi.deleteInquiry(id); refresh(); }} />}
          {tab === "clients" && <ClientTable rows={clients} />}
        </div>
      </main>

      {editing && (
        <PropertyEditor
          editing={editing}
          setEditing={setEditing}
          onSave={saveProperty}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white p-6 border border-slate-200 rounded">
      <div className="text-4xl font-serif">{value}</div>
      <div className="mt-2 text-[11px] uppercase tracking-widest2 text-slate-500 font-semibold">{label}</div>
    </div>
  );
}

function PropertyTable({ rows, onEdit, onDelete }) {
  const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[11px] uppercase tracking-widest2 text-slate-500 font-semibold">
            <th className="p-3">Ref</th>
            <th className="p-3">Title</th>
            <th className="p-3">Type</th>
            <th className="p-3">Location</th>
            <th className="p-3">Flags</th>
            <th className="p-3 text-right">Price</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${p.reference || p.id}`}>
              <td className="p-3 font-mono text-xs">{p.reference || "—"}</td>
              <td className="p-3 max-w-[280px] truncate">{p.title?.en || p.title?.ru || "—"}</td>
              <td className="p-3">{p.type}</td>
              <td className="p-3 text-slate-600">{p.location}</td>
              <td className="p-3 text-xs text-slate-500">
                {[p.off_market && "off", p.exclusive && "excl", p.featured && "feat", p.is_investment && "inv", p.is_rental && "rent"].filter(Boolean).join(" · ")}
              </td>
              <td className="p-3 font-mono text-right">{fmt.format(p.price)}</td>
              <td className="p-3 text-right whitespace-nowrap">
                <button onClick={() => onEdit(p)} data-testid={`admin-edit-${p.reference || p.id}`} className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 mr-3">
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => onDelete(p.id)} data-testid={`admin-delete-${p.reference || p.id}`} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800">
                  <Trash2 size={13} /> Delete
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="p-8 text-center text-slate-400 text-sm">No records.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function InquiryTable({ rows, onDelete }) {
  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[11px] uppercase tracking-widest2 text-slate-500 font-semibold">
            <th className="p-3">Date</th>
            <th className="p-3">Name</th>
            <th className="p-3">Email</th>
            <th className="p-3">Phone</th>
            <th className="p-3">Service</th>
            <th className="p-3">Message</th>
            <th className="p-3">Lang</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i.id} className="border-b border-slate-100 align-top">
              <td className="p-3 font-mono text-xs whitespace-nowrap">{new Date(i.created_at).toLocaleDateString()}</td>
              <td className="p-3">{i.name}</td>
              <td className="p-3"><a href={`mailto:${i.email}`} className="underline">{i.email}</a></td>
              <td className="p-3">{i.phone || "—"}</td>
              <td className="p-3">{i.service || (i.property_id ? "property" : "general")}</td>
              <td className="p-3 max-w-xs truncate" title={i.message}>{i.message}</td>
              <td className="p-3 font-mono text-xs">{i.language}</td>
              <td className="p-3 text-right">
                <button onClick={() => onDelete(i.id)} className="text-xs text-red-600 hover:text-red-800"><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-sm">No inquiries.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ClientTable({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[11px] uppercase tracking-widest2 text-slate-500 font-semibold">
            <th className="p-3">Registered</th>
            <th className="p-3">Name</th>
            <th className="p-3">Email</th>
            <th className="p-3">Phone</th>
            <th className="p-3">Lang</th>
            <th className="p-3 text-right">Favorites</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-b border-slate-100" data-testid={`admin-client-${c.email}`}>
              <td className="p-3 font-mono text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
              <td className="p-3">{c.name}</td>
              <td className="p-3"><a href={`mailto:${c.email}`} className="underline">{c.email}</a></td>
              <td className="p-3">{c.phone || "—"}</td>
              <td className="p-3 font-mono text-xs">{c.language}</td>
              <td className="p-3 text-right font-mono">{c.favorites_count}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-sm">No clients yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PropertyEditor({ editing, setEditing, onSave, onClose }) {
  const upd = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const updNested = (parent, k, v) => setEditing((e) => ({ ...e, [parent]: { ...e[parent], [k]: v } }));
  const toggle = (k) => setEditing((e) => ({ ...e, [k]: !e[k] }));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex justify-end overflow-hidden" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-white h-full overflow-y-auto overscroll-contain scroll-smooth shadow-2xl"
        style={{ WebkitOverflowScrolling: "touch" }}
        data-testid="admin-editor"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest2 text-slate-500">
              {editing.id ? "Edit property" : "New property"}
            </div>
            <div className="font-serif text-xl mt-0.5">{editing.title?.en || editing.reference || "Untitled"}</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} data-testid="admin-cancel" className="text-xs uppercase tracking-widest2 text-slate-600 hover:text-slate-900">Cancel</button>
            <button onClick={onSave} data-testid="admin-save" className="bg-slate-900 text-white text-xs uppercase tracking-widest2 px-5 py-2.5 rounded hover:bg-slate-700">Save</button>
            <button onClick={onClose} className="p-2 text-slate-500 hover:text-slate-900"><X size={16} /></button>
          </div>
        </div>

        <div className="p-8 space-y-10">
          <Section title="Basics">
            <Grid>
              <Field label="Reference *">
                <input required data-testid="pf-reference" className="in" placeholder="JKI-2601" value={editing.reference} onChange={(e) => upd("reference", e.target.value)} />
              </Field>
              <Field label="Type">
                <select data-testid="pf-type" className="in" value={editing.type} onChange={(e) => upd("type", e.target.value)}>
                  <option value="villa">Villa</option>
                  <option value="apartment">Apartment</option>
                  <option value="penthouse">Penthouse</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="finca">Finca</option>
                  <option value="chalet">Chalet</option>
                  <option value="plot">Plot / Land</option>
                  <option value="commercial">Commercial</option>
                  <option value="offmarket">Off-market</option>
                  <option value="investment">Investment</option>
                </select>
              </Field>
              <Field label="Location"><input className="in" data-testid="pf-location" value={editing.location} onChange={(e) => upd("location", e.target.value)} placeholder="Palma / Deià / Sóller…" /></Field>
              <Field label="Country"><input className="in" value={editing.country} onChange={(e) => upd("country", e.target.value)} /></Field>
              <Field label="Price (EUR)"><input type="number" data-testid="pf-price" className="in" value={editing.price} onChange={(e) => upd("price", e.target.value)} /></Field>
              <Field label="Bedrooms"><input type="number" data-testid="pf-bd" className="in" value={editing.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} /></Field>
              <Field label="Bathrooms"><input type="number" data-testid="pf-ba" className="in" value={editing.bathrooms} onChange={(e) => upd("bathrooms", e.target.value)} /></Field>
              <Field label="Area (m²)"><input type="number" className="in" value={editing.area_m2} onChange={(e) => upd("area_m2", e.target.value)} /></Field>
              <Field label="Plot (m²)"><input type="number" className="in" value={editing.plot_m2} onChange={(e) => upd("plot_m2", e.target.value)} /></Field>
              <Field label="Year built"><input type="number" className="in" value={editing.year_built || ""} onChange={(e) => upd("year_built", e.target.value)} placeholder="2022" /></Field>
            </Grid>
          </Section>

          <Section title="Status">
            <div className="flex items-center gap-3">
              <select
                data-testid="pf-status"
                className="in max-w-xs"
                value={editing.status || "available"}
                onChange={(e) => upd("status", e.target.value)}
              >
                <option value="available">Available</option>
                <option value="reserved">Reserved</option>
                <option value="sold">Sold</option>
              </select>
              <span className="text-xs text-slate-500">Отображается на карточке объекта.</span>
            </div>
          </Section>

          <Section title="Flags">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {[["off_market","Off-market"],["exclusive","Exclusive"],["featured","Featured on Home"],["is_investment","Investment"],["investor_closed","Investor NDA-closed"],["is_rental","Rental"]].map(([k,l]) => (
                <Check key={k} label={l} v={editing[k]} onChange={() => toggle(k)} testid={`pf-${k.replace(/_/g,'-')}`} />
              ))}
            </div>
          </Section>

          {editing.is_investment && (
            <Section title="Investment">
              <Grid>
                <Field label="Expected yield"><input className="in" value={editing.expected_yield} onChange={(e) => upd("expected_yield", e.target.value)} placeholder="8-10% p.a." /></Field>
                <Field label="Ticket size"><input className="in" value={editing.ticket_size} onChange={(e) => upd("ticket_size", e.target.value)} placeholder="€500k min" /></Field>
                <Field label="Horizon"><input className="in" value={editing.horizon} onChange={(e) => upd("horizon", e.target.value)} placeholder="3-5 years" /></Field>
              </Grid>
            </Section>
          )}

          {editing.is_rental && (
            <Section title="Rental">
              <Grid>
                <Field label="Rent price (EUR)"><input type="number" className="in" value={editing.rent_price} onChange={(e) => upd("rent_price", e.target.value)} /></Field>
                <Field label="Period">
                  <select className="in" value={editing.rental_period} onChange={(e) => upd("rental_period", e.target.value)}>
                    <option value="week">per week</option>
                    <option value="month">per month</option>
                    <option value="night">per night</option>
                  </select>
                </Field>
              </Grid>
            </Section>
          )}

          <Section title="Amenities">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-1.5 text-sm">
              {AMENITY_KEYS.map((k) => (
                <Check key={k} label={k.replace(/_/g, " ")} v={!!editing[k]} onChange={() => toggle(k)} testid={`pf-amen-${k}`} />
              ))}
            </div>
          </Section>

          <Section title="Title (ES / EN / DE / RU)">
            <div className="grid grid-cols-2 gap-3">
              {["es","en","de","ru"].map((l) => (
                <input key={l} className="in" placeholder={l.toUpperCase()} value={editing.title?.[l] || ""} onChange={(e) => updNested("title", l, e.target.value)} />
              ))}
            </div>
          </Section>

          <Section title="Description (ES / EN / DE / RU)">
            <div className="grid grid-cols-2 gap-3">
              {["es","en","de","ru"].map((l) => (
                <textarea key={l} rows={4} className="in" placeholder={l.toUpperCase()} value={editing.description?.[l] || ""} onChange={(e) => updNested("description", l, e.target.value)} />
              ))}
            </div>
          </Section>

          <Section title="Photos">
            <MediaUploader
              kind="image"
              urls={editing.images || []}
              alts={editing.image_alts || []}
              onChange={(urls, alts) => {
                upd("images", urls);
                if (alts) upd("image_alts", alts);
              }}
              onAltsChange={(alts) => upd("image_alts", alts)}
            />
          </Section>

          <Section title="Videos">
            <MediaUploader kind="video" urls={editing.videos || []} onChange={(urls) => upd("videos", urls)} />
          </Section>

          <Section title="Features (one per line)">
            <textarea rows={4} className="in text-sm" value={Array.isArray(editing.features) ? editing.features.join("\n") : editing.features} onChange={(e) => upd("features", e.target.value)} />
          </Section>

          <Section title="SEO — Search engine optimisation">
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest2 text-slate-500 mb-2">SEO Title — ES / EN / DE / RU (≈60 chars)</div>
                <div className="grid grid-cols-2 gap-3">
                  {["es","en","de","ru"].map((l) => (
                    <input
                      key={l}
                      data-testid={`pf-seo-title-${l}`}
                      className="in"
                      maxLength={70}
                      placeholder={`${l.toUpperCase()} — e.g. "Villa in Deià with sea view | JK Invest"`}
                      value={editing.seo_title?.[l] || ""}
                      onChange={(e) => updNested("seo_title", l, e.target.value)}
                    />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest2 text-slate-500 mb-2">SEO Description — ES / EN / DE / RU (≈155 chars)</div>
                <div className="grid grid-cols-2 gap-3">
                  {["es","en","de","ru"].map((l) => (
                    <textarea
                      key={l}
                      data-testid={`pf-seo-desc-${l}`}
                      rows={2}
                      className="in"
                      maxLength={180}
                      placeholder={`${l.toUpperCase()} — 1-2 sentences shown by Google under the title`}
                      value={editing.seo_description?.[l] || ""}
                      onChange={(e) => updNested("seo_description", l, e.target.value)}
                    />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest2 text-slate-500 mb-2">SEO Keywords (comma-separated)</div>
                <input
                  data-testid="pf-seo-keywords"
                  className="in"
                  placeholder="luxury villa mallorca, deia sea view, off-market spain"
                  value={editing.seo_keywords || ""}
                  onChange={(e) => upd("seo_keywords", e.target.value)}
                />
                <div className="mt-1 text-[11px] text-slate-500">Alt-text for each image is set below each photo in the Photos section.</div>
              </div>
            </div>
          </Section>
        </div>

        <style>{`
          .in{width:100%;background:#fff;border:1px solid #E2E8F0;padding:0.55rem 0.75rem;outline:none;font-size:0.875rem;color:#0f172a;border-radius:4px}
          .in:focus{border-color:#0f172a}
        `}</style>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest2 text-slate-500 font-semibold mb-4">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{children}</div>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest2 text-slate-500 font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}
function Check({ label, v, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer capitalize">
      <input type="checkbox" checked={!!v} onChange={onChange} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

function MediaUploader({ kind, urls, onChange, alts = [], onAltsChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const inputRef = useRef(null);
  const supportsAlts = kind === "image" && typeof onAltsChange === "function";

  function reorder(from, to) {
    if (from == null || to == null || from === to) return;
    const next = [...urls];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    if (supportsAlts) {
      const nextAlts = [...alts];
      while (nextAlts.length < urls.length) nextAlts.push("");
      const [movedAlt] = nextAlts.splice(from, 1);
      nextAlts.splice(to, 0, movedAlt);
      onAltsChange(nextAlts);
    }
  }

  function setAltAt(idx, value) {
    if (!supportsAlts) return;
    const next = [...alts];
    while (next.length < urls.length) next.push("");
    next[idx] = value;
    onAltsChange(next);
  }

  const MAX_IMAGE_MB = 30;
  const MAX_VIDEO_MB = 100;
  // Target size for compressed images. Delivers "instant" page loads
  // (~1.5 MB per photo) while keeping luxury real-estate sharpness.
  const TARGET_BYTES = 1.5 * 1024 * 1024;
  const HARD_MAX_BYTES = 2 * 1024 * 1024;

  async function compressImage(file) {
    // Always run for images — even <2MB files benefit from re-encode to strip
    // metadata and normalize sizes. But skip if already under 400 KB.
    if (file.size < 400 * 1024) return file;
    try {
      const bitmap = await createImageBitmap(file).catch(async () => {
        // Safari fallback via FileReader + Image
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        return await new Promise((res, rej) => {
          const i = new Image();
          i.onload = () => res(i);
          i.onerror = rej;
          i.src = dataUrl;
        });
      });
      const srcW = bitmap.width || bitmap.naturalWidth;
      const srcH = bitmap.height || bitmap.naturalHeight;

      // Progressive downscale + quality ladder until under target.
      // Max side 2560 = Retina-quality on any laptop/tablet display.
      const ladders = [
        { maxSide: 2560, q: 0.90 },
        { maxSide: 2560, q: 0.82 },
        { maxSide: 2200, q: 0.82 },
        { maxSide: 2000, q: 0.78 },
        { maxSide: 1800, q: 0.75 },
        { maxSide: 1600, q: 0.70 },
      ];

      let bestBlob = null;
      for (const step of ladders) {
        const scale = Math.min(1, step.maxSide / Math.max(srcW, srcH));
        const w = Math.round(srcW * scale);
        const h = Math.round(srcH * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(bitmap, 0, 0, w, h);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", step.q));
        if (!blob) continue;
        bestBlob = blob;
        if (blob.size <= TARGET_BYTES) break;
      }

      if (!bestBlob || bestBlob.size > HARD_MAX_BYTES) return bestBlob
        ? new File([bestBlob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" })
        : file;
      const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
      return new File([bestBlob], name, { type: "image/jpeg" });
    } catch {
      return file;
    }
  }

  async function uploadFiles(files) {
    if (!files || !files.length) return;
    setUploading(true);
    const uploaded = [];
    let sig = null;
    try {
      const sigResp = await axios.get(`${API}/admin/upload/signature`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("jk_admin_jwt")}` },
      });
      sig = sigResp.data;
    } catch (e) {
      toast.error("Could not get upload signature");
      setUploading(false);
      return;
    }
    for (const rawFile of files) {
      try {
        const isVideo = (rawFile.type || "").startsWith("video/");
        const limitMb = isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB;
        if (rawFile.size > limitMb * 1024 * 1024) {
          toast.error(`${rawFile.name}: file exceeds ${limitMb} MB limit`);
          continue;
        }
        const file = isVideo ? rawFile : await compressImage(rawFile);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("timestamp", sig.timestamp);
        fd.append("signature", sig.signature);
        fd.append("api_key", sig.api_key);
        fd.append("folder", sig.folder);
        const endpoint = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${isVideo ? "video" : "image"}/upload`;
        const r = await axios.post(endpoint, fd);
        if (r.data?.secure_url) uploaded.push(r.data.secure_url);
      } catch (e) {
        toast.error(e?.response?.data?.error?.message || `Failed to upload ${rawFile.name}`);
      }
    }
    if (uploaded.length) {
      onChange([...(urls || []), ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    }
    setUploading(false);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  }

  function removeAt(idx) {
    const next = [...urls];
    next.splice(idx, 1);
    onChange(next);
    if (supportsAlts) {
      const nextAlts = [...alts];
      nextAlts.splice(idx, 1);
      onAltsChange(nextAlts);
    }
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-colors ${
          dragOver ? "border-slate-900 bg-slate-50" : "border-slate-300 hover:border-slate-500"
        }`}
        data-testid={`upload-zone-${kind}`}
      >
        <UploadCloud size={28} className="mx-auto text-slate-400" />
        <div className="mt-3 text-sm text-slate-600">
          {uploading ? "Uploading…" : `Drop ${kind === "image" ? "photos" : "videos"} here or click to browse`}
        </div>
        <div className="mt-1 text-xs text-slate-400">
          {kind === "image" ? "Originals up to 30 MB — auto-optimised to ~1.5 MB for instant loading" : "MP4, MOV up to 100 MB"}
        </div>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept={kind === "image" ? "image/*" : "video/*"}
          onChange={(e) => uploadFiles(Array.from(e.target.files))}
          data-testid={`upload-input-${kind}`}
        />
      </div>

      {urls.length > 0 && (
        <div className={kind === "image" ? "grid grid-cols-2 md:grid-cols-3 gap-4" : "space-y-3"}>
          {urls.map((u, idx) => {
            const isImage = kind === "image";
            const isDragging = dragIdx === idx;
            const isTarget = overIdx === idx && dragIdx !== null && dragIdx !== idx;
            return (
              <div key={u + idx} className="space-y-1.5">
              <div
                draggable={isImage}
                onDragStart={(e) => {
                  if (!isImage) return;
                  setDragIdx(idx);
                  e.dataTransfer.effectAllowed = "move";
                  try { e.dataTransfer.setData("text/plain", String(idx)); } catch (_) { /* ignore */ }
                }}
                onDragOver={(e) => {
                  if (!isImage || dragIdx === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (overIdx !== idx) setOverIdx(idx);
                }}
                onDragLeave={() => {
                  if (overIdx === idx) setOverIdx(null);
                }}
                onDrop={(e) => {
                  if (!isImage) return;
                  e.preventDefault();
                  e.stopPropagation();
                  reorder(dragIdx, idx);
                  setDragIdx(null);
                  setOverIdx(null);
                }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                className={`relative group border rounded-md overflow-hidden bg-slate-100 transition-all ${
                  isImage ? "cursor-grab active:cursor-grabbing" : ""
                } ${isDragging ? "opacity-40 scale-[0.98]" : "opacity-100"} ${
                  isTarget ? "ring-2 ring-amber-500 border-amber-500" : "border-slate-200"
                }`}
                data-testid={`media-item-${kind}-${idx}`}
              >
                {isImage ? (
                  <img src={u} alt={alts[idx] || ""} className="w-full aspect-[4/3] object-cover pointer-events-none select-none" draggable={false} />
                ) : (
                  <video src={u} controls className="w-full aspect-video bg-black" />
                )}
                {isImage && idx === 0 && (
                  <div className="absolute left-2 top-2 bg-amber-500 text-slate-900 text-[10px] font-semibold uppercase tracking-widest2 px-2 py-1 rounded shadow-sm" data-testid={`cover-badge-${idx}`}>
                    Cover
                  </div>
                )}
                {isImage && (
                  <div className="absolute left-2 bottom-2 bg-black/60 text-white text-[11px] font-mono px-2 py-0.5 rounded">
                    #{idx + 1}
                  </div>
                )}
                <div className="absolute inset-x-0 top-0 flex items-center justify-between px-1.5 py-1 bg-gradient-to-b from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  {isImage ? (
                    <span className="text-white/90 p-1" title="Drag to reorder"><GripVertical size={16} /></span>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    data-testid={`media-remove-${kind}-${idx}`}
                    className="text-white/90 hover:text-red-400 p-1"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
              {supportsAlts && (
                <input
                  type="text"
                  value={alts[idx] || ""}
                  onChange={(e) => setAltAt(idx, e.target.value)}
                  placeholder="Alt text (SEO — describe the image)"
                  data-testid={`image-alt-${idx}`}
                  className="w-full text-xs bg-white border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-slate-500"
                  maxLength={140}
                />
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
